import { COLOR_CSS_VAR_NAME_CONFIG } from './css-var-name-config'
import { OWN_COLOR } from './own'

type LightnessShadeTenantToOwn = {
  default: string
  [extraTenantShade: string]: string
}

const TENANT_COLOR: Record<
  string,
  {
    ownColorGroupName: string
    lightnessShadeTenantToOwn: LightnessShadeTenantToOwn
  }
> = {
  'body-bg': {
    ownColorGroupName: 'neutral',
    lightnessShadeTenantToOwn: {
      default: 'fill-background-secondary',
    },
  },
  'header-bg': {
    ownColorGroupName: 'white',
    lightnessShadeTenantToOwn: {
      default: 'default',
    },
  },
  'footer-bg': {
    ownColorGroupName: 'white',
    lightnessShadeTenantToOwn: {
      default: 'default',
    },
  },
  'primary': {
    ownColorGroupName: 'brand',
    lightnessShadeTenantToOwn: {
      'fill-pressed': 'fill-pressed',
      'fill-hover': 'fill-hover',
      'default': 'default',
      'stroke-primary': 'stroke-primary',
      'fill-primary': 'fill-primary',
    },
  },
  'primary-inverted': {
    ownColorGroupName: 'brand',
    lightnessShadeTenantToOwn: {
      'fill-pressed': '99',
      'fill-hover': '98',
      'default': 'fill-primary',
      'stroke-primary': 'stroke-primary',
      'fill-primary': '60',
    },
  },
  'primary-inverted-button-label': {
    ownColorGroupName: 'neutral',
    lightnessShadeTenantToOwn: {
      default: 'default',
    },
  },
  'primary-text': {
    ownColorGroupName: 'neutral',
    lightnessShadeTenantToOwn: {
      default: 'default',
    },
  },
  'primary-text-inverted': {
    ownColorGroupName: 'white',
    lightnessShadeTenantToOwn: {
      default: 'default',
    },
  },
  'danger': {
    ownColorGroupName: 'danger',
    lightnessShadeTenantToOwn: {
      'default': 'default',
      'fill-stroke-secondary': 'fill-stroke-secondary',
    },
  },
  'success': {
    ownColorGroupName: 'success',
    lightnessShadeTenantToOwn: {
      'default': 'default',
      'fill-stroke-secondary': 'fill-stroke-secondary',
    },
  },
  'warning': {
    ownColorGroupName: 'warning',
    lightnessShadeTenantToOwn: {
      'default': 'default',
      'fill-stroke-secondary': 'fill-stroke-secondary',
    },
  },
  'link': {
    ownColorGroupName: 'brand',
    lightnessShadeTenantToOwn: {
      default: 'default',
    },
  },
  'secondary': {
    ownColorGroupName: 'neutral',
    lightnessShadeTenantToOwn: {
      default: 'text-icons-secondary',
    },
  },
}

const constructTenantColorHslCoords = ({
  tenantColorGroupName,
  ownColorGroupName,
  lightnessShadeTenantToOwn,
}: {
  tenantColorGroupName: string
  ownColorGroupName: string
  lightnessShadeTenantToOwn: LightnessShadeTenantToOwn
}): React.CSSProperties => {
  const tenantVarNameBase = COLOR_CSS_VAR_NAME_CONFIG.tenant
  const tenantRawVarNameBase = COLOR_CSS_VAR_NAME_CONFIG.tenantRaw
  const ownVarNameBase = COLOR_CSS_VAR_NAME_CONFIG.own

  const hueDecl = {
    [`${tenantVarNameBase}-h-${tenantColorGroupName}`]: `var(${tenantRawVarNameBase}-h-${tenantColorGroupName}, var(${ownVarNameBase}-h-${ownColorGroupName}))`,
  }

  const saturationDecl = {
    [`${tenantVarNameBase}-s-${tenantColorGroupName}`]: `var(${tenantRawVarNameBase}-s-${tenantColorGroupName}, var(${ownVarNameBase}-s-${ownColorGroupName}))`,
  }

  const lightnessShadeTenantToOwnEntries = Object.entries(lightnessShadeTenantToOwn)

  const getTenantVarName = (shadeName: string) => `${tenantVarNameBase}-l-${tenantColorGroupName}-${shadeName}`

  const getTenantVarValue = (targetOwnLightness: number, defaultOwnLightness: number) => {
    const ownDelta = targetOwnLightness - defaultOwnLightness
    const isLighterThanDefault = ownDelta > 0
    const ownRange = isLighterThanDefault ? 100 - defaultOwnLightness : defaultOwnLightness
    const fraction = Math.abs(ownDelta) / ownRange

    return isLighterThanDefault
      ? `calc(var(${defaultTenantVarName}) + (100% - var(${defaultTenantVarName})) * ${fraction})`
      : `calc(var(${defaultTenantVarName}) * (1 - ${fraction}))`
  }

  const defaultTenantVarName = getTenantVarName('default')

  const lightnessDeclEntries = lightnessShadeTenantToOwnEntries.map(([tenantShadeName, ownShadeName]) => {
    if (tenantShadeName === 'default') {
      return [
        defaultTenantVarName,
        `var(${tenantRawVarNameBase}-l-${tenantColorGroupName}, var(${ownVarNameBase}-l-${ownColorGroupName}-${ownShadeName}))`,
      ]
    }

    const tenantVarName = getTenantVarName(tenantShadeName)

    const ownShadeLightness = OWN_COLOR[ownColorGroupName].l[ownShadeName]
    const defaultOwnLightnessShadeName = lightnessShadeTenantToOwn.default
    const defaultOwnShadeLightness = OWN_COLOR[ownColorGroupName].l[defaultOwnLightnessShadeName]

    const tenantVarValue = getTenantVarValue(ownShadeLightness, defaultOwnShadeLightness)

    return [tenantVarName, tenantVarValue]
  })

  return {
    ...hueDecl,
    ...saturationDecl,
    ...Object.fromEntries(lightnessDeclEntries),
  }
}

const getTenantHslColorInlineStyles = (): React.CSSProperties => {
  const tenantGroupEntries = Object.entries(TENANT_COLOR)
  const tenantGroupStyleDecls = tenantGroupEntries.map(
    ([tenantColorGroupName, { ownColorGroupName, lightnessShadeTenantToOwn }]) => {
      return constructTenantColorHslCoords({ tenantColorGroupName, ownColorGroupName, lightnessShadeTenantToOwn })
    },
  )

  return tenantGroupStyleDecls.reduce((output, styleDecl) => {
    return {
      ...output,
      ...styleDecl,
    }
  }, {})
}

export const tenantColorInlineStyles = {
  getHsl: getTenantHslColorInlineStyles,
}
